import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useAuth } from '../auth/auth-hook';
import { AvatarSettings } from '../setting/components/SettingsStyledComponents';
import { InputDefault } from '../../shared/components/inputs/InputDefault';
import { useNavigate, useParams } from 'react-router-dom';
import { ButtonDefault } from '../../shared/components/ButtonDefault';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { userExtraDetailsSchema } from './user-validator';
import { handleFormErrors } from '../../shared/utils/errors';
import { useCallAction } from '@cobuildlab/react-simple-state';
import { openSnackbarAction } from '../../shared/components/snackbar/snackbar-actions';
import { TypeMessage } from '../../shared/components/snackbar/snackbar-types';
import { SelectDefault } from '../../shared/components/inputs/SelectDefault';
import { Loading } from '../../shared/components/Loading';
import { GlobalUserExtraData } from './user-types';
import { UserRoles } from '../../shared/types/generated';
import { PROJECT_COLOR } from '../../shared/constants';
import { fetchGlobalExtraUserAction, updateGlobalUserExtra } from './user-actions';
import { Interests, translateInterest } from '../../shared/utils';

export const UserExtraDetails: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [tenantUserDetails, setTenantUserDetails] = useState<GlobalUserExtraData | null>(null);
  const [loadingFetch, setLoadingFetch] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const isEditable = user?.roles_on_users?.find(
    (roles) => roles?.role?.name === UserRoles.Administrator,
  );

  const { control, formState, handleSubmit, setValue } = useForm({
    resolver: yupResolver(userExtraDetailsSchema, { abortEarly: false }),
  });

  const [callFetch] = useCallAction(fetchGlobalExtraUserAction, {
    onCompleted: (data) => {
      setTenantUserDetails(data);
      setValue('address', data.subscription_billing?.address || ''); //Street name
      setValue('external_number', data.subscription_billing?.external_number || ''); // external number
      setValue('internal_number', data.subscription_billing?.internal_number || ''); // internal number
      setValue('address1', data.subscription_billing?.address1 || ''); // colonia
      setValue('city', data.subscription_billing?.city || '');
      setValue('state', data.subscription_billing?.state || '');
      setValue('country', data.subscription_billing?.country || '');
      setValue('postal_code', data.subscription_billing?.postal_code || '');
      setValue('phone', data.subscription_billing?.phone || '');
      setValue('phone1', data.subscription_billing?.phone1 || '');
      setValue('interests', data.interests.map((item) => item.interest) || []);
      setValue('wppbroadcastChannel', data.broadcast_user_data?.must_send !== undefined ? data.broadcast_user_data?.must_send :undefined);
      setTimeout(() => {
        setLoadingFetch(false);
      }, 1000);
    },
    onError: (error) => {
      openSnackbarAction(error.message, TypeMessage.ERROR);
    },
  });

  const [callUpdateUser, loading] = useCallAction(updateGlobalUserExtra, {
    onCompleted: () => {
      openSnackbarAction('Usuario actualizado correctamente', TypeMessage.SUCCESS);
      if (id) {
        setLoadingFetch(true);
        callFetch({
          id,
        });
      }
    },
    onError: (error) => {
      openSnackbarAction(error.message, TypeMessage.ERROR);
      setTimeout(() => {
        navigate('/users');
      }, 2000);
    },
  });

  useEffect(() => {
    if (id) {
      setLoadingFetch(true);
      callFetch({
        id,
      });
    }
  }, [callFetch, id]);

  const onFormSubmit = handleSubmit(
    async (dataForm) => {
      callUpdateUser({
        user: { ...dataForm },
        userId: tenantUserDetails?.id ?? '',
        file,
      });
    },
    (error) => {
      handleFormErrors(error);
    },
  );

  return (
    <Container maxWidth={'lg'}>
      <Typography
        fontSize={24}
        fontWeight={'bold'}
        textAlign={'center'}
        textTransform={'uppercase'}
      >
        Detalles para suscripción
      </Typography>
      <Box paddingTop={'24px'} component="form" onSubmit={onFormSubmit}>
        {loadingFetch || !tenantUserDetails ? (
          <Loading height="400px" />
        ) : (
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              md={6}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
            >
              <Box flex={1}>
                <Typography textAlign={'start'}>Credencial estudiantil</Typography>
                <input
                  type="file"
                  hidden
                  disabled={!isEditable}
                  ref={fileRef}
                  onChange={(event) => {
                    setFile(
                      event.target.files && event.target.files.length
                        ? event.target.files[0]
                        : null,
                    );
                  }}
                />
                <AvatarSettings
                  sx={{
                    width: { xs: '100%' },
                    height: { xs: 450 },
                    borderRadius: '10px',
                    objectFit: 'contain',
                  }}
                  onClick={() => fileRef.current?.click()}
                  imgProps={{ sx: { objectFit: 'contain' } }}
                  src={
                    file
                      ? URL.createObjectURL(file)
                      : tenantUserDetails?.student_credential?.student_card ?? ''
                  }
                />
                {tenantUserDetails?.student_credential?.student_card ? (
                  <Box paddingTop={2}>
                    <Typography>Url de la Credencial:</Typography>
                    <Typography
                      component={'a'}
                      target='_blank'
                      href={tenantUserDetails?.student_credential?.student_card || ''}
                    >
                      {tenantUserDetails?.student_credential?.student_card}{' '}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name={'interests'}
                    control={control}
                    render={({ field }) => (
                      <>
                        <InputLabel sx={{ color: '#000000' }} id="interests-id">
                          Intereses
                        </InputLabel>
                        <Select
                          labelId="interests-id"
                          variant="outlined"
                          sx={{ width: '100%' }}
                          //labelName="Intereses"
                          label="Intereses"
                          multiple
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                              {selected?.map((value) => <Chip key={value} label={value} />)}
                            </Box>
                          )}
                          //helperText={formState.errors.interests?.message}
                          error={!!formState.errors.interests?.message}
                          inputProps={{ readOnly: !isEditable }}
                          {...field}
                        >
                          {Object.values(Interests)
                            .filter((item) => item !== Interests.Newsweek)
                            .map((item) => {
                              return {
                                value: translateInterest(item),
                                label: item,
                              };
                            })
                            .map(({ label, value }) => (
                              <MenuItem key={label} value={value}>
                                {label}
                              </MenuItem>
                            ))}
                        </Select>
                      </>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={'phone1'}
                    control={control}
                    render={({ field }) => (
                      <InputDefault
                        variant="outlined"
                        labelName="Número de teléfono adicional"
                        helperText={formState.errors.external_number?.message}
                        error={!!formState.errors.external_number?.message}
                        inputProps={{ readOnly: !isEditable }}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={'address'}
                    control={control}
                    render={({ field }) => (
                      <InputDefault
                        variant="outlined"
                        labelName="Nombre de calle"
                        //endIcon={<PersonIcon />}
                        helperText={formState.errors.address?.message}
                        error={!!formState.errors.address?.message}
                        inputProps={{ readOnly: !isEditable }}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={'external_number'}
                    control={control}
                    render={({ field }) => (
                      <InputDefault
                        variant="outlined"
                        labelName="Número exterior"
                        helperText={formState.errors.external_number?.message}
                        error={!!formState.errors.external_number?.message}
                        inputProps={{ readOnly: !isEditable }}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={'internal_number'}
                    control={control}
                    render={({ field }) => (
                      <InputDefault
                        variant="outlined"
                        labelName="Número interior"
                        helperText={formState.errors.external_number?.message}
                        error={!!formState.errors.external_number?.message}
                        inputProps={{ readOnly: !isEditable }}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={'address1'}
                    control={control}
                    render={({ field }) => (
                      <InputDefault
                        variant="outlined"
                        labelName="Colonia"
                        helperText={formState.errors.external_number?.message}
                        error={!!formState.errors.external_number?.message}
                        inputProps={{ readOnly: !isEditable }}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name={'city'}
                control={control}
                render={({ field }) => (
                  <InputDefault
                    variant="outlined"
                    labelName="Ciudad"
                    helperText={formState.errors.external_number?.message}
                    error={!!formState.errors.external_number?.message}
                    inputProps={{ readOnly: !isEditable }}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={'state'}
                control={control}
                render={({ field }) => (
                  <InputDefault
                    variant="outlined"
                    labelName="Estado"
                    helperText={formState.errors.external_number?.message}
                    error={!!formState.errors.external_number?.message}
                    inputProps={{ readOnly: !isEditable }}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={'country'}
                control={control}
                render={({ field }) => (
                  <InputDefault
                    variant="outlined"
                    labelName="País"
                    helperText={formState.errors.external_number?.message}
                    error={!!formState.errors.external_number?.message}
                    inputProps={{ readOnly: !isEditable }}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={'postal_code'}
                control={control}
                render={({ field }) => (
                  <InputDefault
                    variant="outlined"
                    labelName="Código postal"
                    helperText={formState.errors.external_number?.message}
                    error={!!formState.errors.external_number?.message}
                    inputProps={{ readOnly: !isEditable }}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={'phone'}
                control={control}
                render={({ field }) => (
                  <InputDefault
                    variant="outlined"
                    labelName="Número de teléfono de contacto"
                    helperText={formState.errors.external_number?.message}
                    error={!!formState.errors.external_number?.message}
                    inputProps={{ readOnly: !isEditable }}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={'wppbroadcastChannel'}
                control={control}
                render={({ field }) => (
                  <SelectDefault
                    variant="outlined"
                    labelName="Suscrito al canal de difusión de Whatsapp"
                    helperText={formState.errors.wppbroadcastChannel?.message}
                    error={!!formState.errors.wppbroadcastChannel?.message}
                    inputProps={{ readOnly: !isEditable }}
                    options={[
                      {
                        value: 'true',
                        label: 'Si',
                      },
                      {
                        value: 'false',
                        label: 'No',
                      },
                    ]}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box textAlign="end">
                {isEditable && (
                  <ButtonDefault
                    isLoading={loading}
                    type="submit"
                    startIcon={
                      !loading ? (
                        <img src="/icon-update.svg" alt="clip" width={14} height={14} />
                      ) : null
                    }
                    style={{
                      width: 190,
                      height: 40,
                      textTransform: 'inherit',
                      fontWeight: 700,
                      background: (PROJECT_COLOR ?? '#000000') + '20 !important',
                    }}
                  >
                    ACTUALIZAR
                  </ButtonDefault>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Container>
  );
};
